<template>
  <div class="lfn-container">
    <div class="lfn-header-container">
      <h1>{{ chartTitle }}</h1>
      <div v-if="loaded" class="info-container">
        <div class="info-item">
          <span>dBC - dBA:</span>
          <span><strong>{{ CmA }} dB</strong></span>
        </div>
        <div class="info-item">
          <span>LFN Threshold Exceedance:</span>
          <strong>{{ threshold_exceedance }}</strong>
        </div>
        <div class="info-item">
          <span>Adjustment Applied:</span>
          <strong>{{ adjustment_applied }}</strong>
        </div>
      </div>
    </div>
    <div class="lfn-chart-wrapper">
      <div v-if="noLFNData" class="no-data-message">
        <p>No low frequency noise data available for the selected parameters.</p>
      </div>
      <v-chart v-if="loaded && !noLFNData" :option="chartOption" class="chart-container" autoresize />
    </div>
    <div v-if="!loaded" class="whiteout-overlay">
      <img src="@/assets/svg/12-dots-scale-rotate-magecdn.svg" alt="Loading..." class="lfn-spinner" />
    </div>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "../keycloak-wrapper.ts";
import VChart from "vue-echarts";
import { bankersRound } from "../helpers";

export default {
  name: "LowFrequencyNoise",
  components: {
    VChart,
  },
  props: {
    APIparams: {
      type: Object,
      required: true,
    },
    regions: {
      type: Array,
      required: true,
    },
    sourceNames: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      abortController: null,
      rawChartData: null,
      loaded: false,
      noLFNData: false,
      chartOption: {},
      CmA: "",
      threshold_exceedance: "",
      adjustment_applied: "",
    };
  },
  computed: {
    chartTitle() {
      return this.sourceNames.length > 0
        ? `Low Frequency Noise (${this.sourceNames.join(', ')})`
        : 'Low Frequency Noise (Ambient)';
    },
  },
  methods: {
    async fetchData() {
      // abort any in-flight request before starting a new one
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController()

      this.loaded = false;
      try {
        const token = await getToken();
        const response = await axios.post(
          api_config.backend + "/lowFrequencyNoise",
          {
            unit_ID: this.APIparams.unit_ID,
            date_time: this.APIparams.date_time,
            regions: this.regions
          },
          {
            signal: this.abortController.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        this.rawChartData = response.data;
        // Process info data
        const info = response.data.info;
        const spectraCorrections = Number(info.correction_required ?? 0);
        const maxExceedanceVal = Number(Object.values(info.max_exceedance)[0] ?? 0);
        const exceedanceBand = Object.keys(info.max_exceedance)[0];
        if (maxExceedanceVal > 0) {
          this.threshold_exceedance = `${maxExceedanceVal.toFixed(1)} dB at ${exceedanceBand}`;
          this.adjustment_applied = `+${spectraCorrections} dB`;
        } else {
          this.threshold_exceedance = "None";
          this.adjustment_applied = "0 dB";
        }
        this.CmA = bankersRound(Number(info?.C_minus_A ?? 0), 1);   // Currently this does nothing since the backend returns a 1dp float, but if that logic changes this forces the same formattting
        this.loaded = true;
        this.noLFNData = false;
        this.updateChart();
      } catch (error) {
        if (error.response?.status === 422){
          this.noLFNData = true;
          this.loaded = true;
        } else {
          console.error("Error fetching low frequency noise data:", error);
        }
      }
    },
    normalizeBandLabel(key) {
      // 1) Remove any "LF" prefix
      const label = key.replace(/^LF/, "");
      // 2) Remove trailing colon if present
      // label = label.replace(/:$/, '')
      // 3) (Optionally) ensure consistent units:
      //    If it says "kHz", leave it;
      //    otherwise you could append "Hz" here, or
      //    convert "1.0kHz" → "1000"
      return label;
    },
    updateChart() {
      if (!this.loaded || !this.rawChartData) return;
      const spectra = this.rawChartData.spectra;
      const octaveBands = Object.keys(spectra).map((key) =>
        this.normalizeBandLabel(key)
      );
      const correction = Number(this.rawChartData.info.correction_required ?? 0);
      const spectraData = Object.keys(spectra).map((key) => {
        const value = spectra[key];
        return Number(value["0"] ?? 0) + correction;
      });
      const thresholds = Object.values(this.rawChartData.thresholds);
      // Set the chart option for the v-chart component.
      this.chartOption = {
        //   title: {
        //     text: "Low Frequency Noise"
        //   },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          shadowStyle: {
            color: "rgba(0,0,0,0.5)",
          },
          formatter: function (param) {
            // console.log('param', param)
            let content = `${param[0].name}</b><br/>${param[0].marker
              }Spectra: ${bankersRound(param[0].value)}</b><br/>`;
            if (param[1]) {
              content += `${param[1].marker}${param[1].seriesName
                }: ${bankersRound(param[1].value)}`;
            }
            return content;
          },
        },
        xAxis: {
          type: "category",
          data: octaveBands,
          name: "Octave Bands",
        },
        yAxis: {
          type: "value",
          name: "dB",
          min: 0,
        },
        grid: {
          left: 30,
          top: 10,
          right: 0,
          bottom: 20,
        },
        series: [
          {
            type: "bar",
            data: spectraData,
            itemStyle: {
              color: "#009B69",
              opacity: 0.6,
            },
            name: "LZeq",
          },
          {
            type: "line",
            data: thresholds,
            itemStyle: {
              color: "black",
            },
            name: "LFN Threshold",
          },
        ],
      };
    },
  },
  watch: {
    APIparams: {
      deep: true,
      handler() {
        this.fetchData();
      },
    },
    regions: {
      deep: true,
      handler() {
        this.fetchData();
      },
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.lfn-container {
  padding: 0px;
  background-color: var(--colour-bg-light);
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.lfn-header-container {
  height: 120px;
  position: relative;
  z-index: 5;
}

.info-container {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  background-color: var(--color-secondary);
  border-radius: 8px;
  color: var(--colour-surface);
  height: 75px;
}

.info-item {
  padding: 0 5px;
  display: flex;
  width: 100%;
  text-align: center;
  justify-content: space-between;
}
.lfn-chart-wrapper {
  position: relative;
  flex: 1;
  min-height: 0;
}

.chart-container {
  width: 100%;
  height: 100%;
}

.lfn-spinner {
  width: 40%;
  height: 40%;
}

.no-data-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--colour-text-body-muted);
  font-size: var(--font-size-md);
  text-align: center;
  padding: var(--spacing-ml);
}

</style>
